import React, { useState } from 'react';
import { BsClockHistory } from 'react-icons/bs';
import { MdCheckCircle, MdCancel, MdAccessTime } from 'react-icons/md';
import { FaCalendarAlt } from 'react-icons/fa';
import { GrNotes } from 'react-icons/gr';
import type { LeaveRequest } from '../../redux/types/attendanceTypes';
import { formatDate } from './shared';
import PopupWrapper from '../utils/PopupWrapper';

interface Props {
  requests: LeaveRequest[];
}

const STATUS_STYLE = {
  PENDING:  { cls: 'bg-gray-100 text-gray-500 border border-gray-200', icon: <MdAccessTime className="text-sm" />,  label: 'Pending'  },
  APPROVED: { cls: 'bg-green-100 text-green-700',                      icon: <MdCheckCircle className="text-sm" />, label: 'Approved' },
  REJECTED: { cls: 'bg-red-100 text-red-600',                          icon: <MdCancel className="text-sm" />,      label: 'Rejected' },
};

const dayCount = (start: string, end: string) =>
  Math.max(1, Math.round((new Date(end).getTime() - new Date(start).getTime()) / 86_400_000) + 1);

// ── Detail popup ──────────────────────────────────────────────────────────────

const LeaveDetailPopup: React.FC<{ item: LeaveRequest; onClose: () => void }> = ({ item, onClose }) => {
  const s       = STATUS_STYLE[item.status] ?? STATUS_STYLE.PENDING;
  const days    = dayCount(item.startDate, item.endDate);
  const reviewer = item.reviewedByAdmin?.fullname ?? item.reviewedBy?.fullname;

  return (
    <PopupWrapper
      isOpen
      onClose={onClose}
      title="Leave Request"
      size="md"
      logo={
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 bg-amber-100 text-amber-700">
          L
        </div>
      }
      headerComponent={
        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${s.cls}`}>
          {s.icon} {s.label}
        </span>
      }
      footer={
        reviewer || item.reviewNote ? (
          <div className="text-sm text-gray-500 space-y-1">
            {reviewer && (
              <p>Reviewed by: <span className="font-semibold text-gray-700">{reviewer}</span></p>
            )}
            {item.reviewNote && (
              <p className="italic text-gray-400">"{item.reviewNote}"</p>
            )}
          </div>
        ) : undefined
      }
    >
      {/* Half-day period badge */}
      {item.halfDayPeriod && (
        <div className="flex justify-center mb-4">
          <span className={`inline-flex items-center text-xs font-semibold px-3 py-1 rounded-full ${
            item.halfDayPeriod === 'FIRST_HALF'
              ? 'bg-indigo-100 text-indigo-600'
              : 'bg-purple-100 text-purple-600'
          }`}>
            {item.halfDayPeriod === 'FIRST_HALF' ? 'First Half (Morning)' : 'Second Half (Afternoon)'}
          </span>
        </div>
      )}

      {/* Date range */}
      <div className="flex justify-between items-center mb-8 text-[#4D4D4D] px-2 md:px-4 mt-2">
        <div>
          <p className="text-xs text-gray-400">From</p>
          <div className="flex items-center gap-2">
            <FaCalendarAlt className="text-xs text-gray-800" />
            <p className="font-semibold text-sm text-gray-900 whitespace-nowrap">{formatDate(item.startDate)}</p>
          </div>
        </div>
        <div className="text-center">
          <p className="text-[#999999] tracking-widest text-[10px]">{days} {days === 1 ? 'DAY' : 'DAYS'}</p>
          <p className="text-[4px] text-gray-300">──────────────────────────────────────────────────</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-400">To</p>
          <div className="flex items-center gap-2">
            <FaCalendarAlt className="text-xs text-gray-800" />
            <p className="font-semibold text-sm text-gray-900 whitespace-nowrap">{formatDate(item.endDate)}</p>
          </div>
        </div>
      </div>

      {/* Fields */}
      <div className="space-y-6">
        <div className="relative">
          <p className="absolute -top-2 left-4 bg-white px-2 text-xs text-[#B3B3B3] flex items-center gap-1">
            <FaCalendarAlt /> Requested on
          </p>
          <div className="border border-[#8D99A5] rounded-lg p-4 text-sm text-[#8D99A5] pl-6">
            {item.createdAt ? formatDate(item.createdAt) : '—'}
          </div>
        </div>

        <div className="relative">
          <p className="absolute -top-2 left-4 bg-white px-2 text-xs text-[#B3B3B3] flex items-center gap-1">
            <GrNotes /> Reason / Description
          </p>
          <div className="border border-[#8D99A5] rounded-lg p-4 text-sm text-[#8D99A5] pl-6 min-h-[64px]">
            {item.reason || '—'}
          </div>
        </div>
      </div>
    </PopupWrapper>
  );
};

// ── Sidebar ───────────────────────────────────────────────────────────────────

const EmployeeLeaveHistory: React.FC<Props> = ({ requests }) => {
  const [selected, setSelected] = useState<LeaveRequest | null>(null);

  return (
    <>
      <div className="bg-white shadow border rounded-xl w-full h-auto md:h-[460px] xl:h-[520px] flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 border-b flex items-center gap-2 flex-shrink-0">
          <BsClockHistory className="text-xl text-gray-600 flex-shrink-0" />
          <h2 className="text-base font-semibold text-gray-800">Leave History</h2>
          {requests.length > 0 && (
            <span className="ml-auto text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">
              {requests.length}
            </span>
          )}
        </div>

        {/* List */}
        <div className="overflow-y-auto flex-1 px-3 py-2">
          {requests.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No leave requests.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {requests.map(r => {
                const s      = STATUS_STYLE[r.status] ?? STATUS_STYLE.PENDING;
                const days   = dayCount(r.startDate, r.endDate);

                return (
                  <button
                    key={r.id}
                    onClick={() => setSelected(r)}
                    className="border border-gray-100 rounded-xl p-3 text-left hover:bg-gray-50 hover:border-gray-200 transition-colors w-full"
                  >
                    {/* Type + status */}
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-amber-50 text-amber-600">
                        Leave
                      </span>
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${s.cls}`}>
                        {s.icon} {s.label}
                      </span>
                    </div>

                    {/* Dates */}
                    <div className="flex items-start gap-1 text-xs mb-1">
                      <div>
                        <p className="text-[10px] text-gray-400">From</p>
                        <p className="font-semibold text-gray-800">{formatDate(r.startDate)}</p>
                      </div>
                      <div className="flex-1 flex flex-col items-center pt-3 px-1">
                        <div className="w-full border-t border-dashed border-gray-200" />
                        <span className="text-[10px] text-gray-400 mt-0.5">{days}d</span>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] text-gray-400">To</p>
                        <p className="font-semibold text-gray-800">{formatDate(r.endDate)}</p>
                      </div>
                    </div>

                    {/* Half-day period badge */}
                    {r.halfDayPeriod && (
                      <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full mt-1 ${
                        r.halfDayPeriod === 'FIRST_HALF'
                          ? 'bg-indigo-100 text-indigo-600'
                          : 'bg-purple-100 text-purple-600'
                      }`}>
                        {r.halfDayPeriod === 'FIRST_HALF' ? 'First Half' : 'Second Half'}
                      </span>
                    )}

                    {/* Reason preview */}
                    {r.reason && (
                      <p className="text-[11px] text-gray-400 truncate mt-1.5 italic">"{r.reason}"</p>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Detail popup */}
      {selected && (
        <LeaveDetailPopup item={selected} onClose={() => setSelected(null)} />
      )}
    </>
  );
};

export default EmployeeLeaveHistory;
